import { useLanguage } from '../context/LanguageContext';
import { useTheme } from '../context/ThemeContext';
import { Trash2, Mail, Clock, ShieldAlert, FileCheck, CheckCircle2, AlertTriangle, ArrowLeft, Copy } from 'lucide-react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';

export default function UserDataDeletion() {
  const { language } = useLanguage();
  const { isDarkMode } = useTheme();

  const SUPPORT_EMAIL = 'support@cloudgm.business';
  const EMAIL_SUBJECT = language === 'ar'
    ? 'طلب حذف بيانات الحساب'
    : 'Account Data Deletion Request';
  const EMAIL_BODY = language === 'ar'
    ? `مرحباً فريق CloudGM Business،%0D%0A%0D%0Aأرغب في حذف حسابي وجميع بياناتي الشخصية من المنصة.%0D%0A%0D%0A- البريد الإلكتروني للحساب: %0D%0A- رقم الهاتف: %0D%0A- سبب الحذف (اختياري): %0D%0A%0D%0Aأؤكد أنني قرأت سياسة الخصوصية وأفهم أن هذه العملية غير قابلة للتراجع.%0D%0A%0D%0Aشكراً لكم.`
    : `Hello CloudGM Business Team,%0D%0A%0D%0AI would like to delete my account and all my personal data from the platform.%0D%0A%0D%0A- Account Email: %0D%0A- Phone Number: %0D%0A- Reason (optional): %0D%0A%0D%0AI confirm that I have read the Privacy Policy and understand that this action is irreversible.%0D%0A%0D%0AThank you.`;

  const mailtoLink = `mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(EMAIL_SUBJECT)}&body=${EMAIL_BODY}`;

  const handleCopyEmail = () => {
    navigator.clipboard.writeText(SUPPORT_EMAIL);
    toast.success(language === 'ar' ? 'تم نسخ البريد الإلكتروني' : 'Email copied to clipboard');
  };

  const steps = [
    {
      icon: Mail,
      title: language === 'ar' ? 'أرسل بريداً إلكترونياً' : 'Send an Email',
      content: language === 'ar'
        ? 'أرسل بريداً إلكترونياً إلى support@cloudgm.business من نفس البريد المرتبط بحسابك مع كتابة "طلب حذف بيانات الحساب" في العنوان.'
        : 'Send an email to support@cloudgm.business from the same email associated with your account, with the subject "Account Data Deletion Request".'
    },
    {
      icon: FileCheck,
      title: language === 'ar' ? 'أكّد هويتك' : 'Verify Your Identity',
      content: language === 'ar'
        ? 'أدرج في البريد: بريدك الإلكتروني، ورقم الهاتف المسجّل، وأي معلومات إضافية تساعدنا على التحقق من ملكيتك للحساب.'
        : 'Include in the email: your account email, registered phone number, and any additional information that helps us verify account ownership.'
    },
    {
      icon: Clock,
      title: language === 'ar' ? 'انتظر التأكيد' : 'Wait for Confirmation',
      content: language === 'ar'
        ? 'سنراجع طلبك ونرد عليك خلال 3 إلى 5 أيام عمل لتأكيد استلامه. ستُنفَّذ عملية الحذف الكامل خلال 30 يوماً كحد أقصى.'
        : 'We will review your request and respond within 3 to 5 business days to confirm receipt. The full deletion process will be completed within a maximum of 30 days.'
    },
    {
      icon: CheckCircle2,
      title: language === 'ar' ? 'اكتمال الحذف' : 'Deletion Completed',
      content: language === 'ar'
        ? 'بعد الاكتمال، ستتلقى بريداً إلكترونياً بتأكيد حذف جميع بياناتك الشخصية من أنظمتنا. قد نحتفظ ببعض السجلات لأغراض قانونية أو محاسبية فقط.'
        : 'Upon completion, you will receive a confirmation email that all your personal data has been removed from our systems. We may retain certain records for legal or accounting purposes only.'
    }
  ];

  const dataToBeDeleted = [
    language === 'ar' ? 'معلومات الحساب الشخصية (الاسم، البريد، الهاتف)' : 'Personal account information (name, email, phone)',
    language === 'ar' ? 'المتاجر والمنتجات والقوائم المرتبطة بالحساب' : 'Stores, products, and listings linked to the account',
    language === 'ar' ? 'محادثات الدعم والرسائل الداخلية' : 'Support conversations and internal messages',
    language === 'ar' ? 'سجل المحادثات مع الذكاء الاصطناعي' : 'AI chat history',
    language === 'ar' ? 'الإشعارات والتفضيلات' : 'Notifications and preferences',
    language === 'ar' ? 'الصور والملفات المرفوعة' : 'Uploaded images and files'
  ];

  const dataRetained = [
    language === 'ar' ? 'سجلات المعاملات المالية (لالتزامات ضريبية / قانونية)' : 'Financial transaction records (for tax / legal obligations)',
    language === 'ar' ? 'سجلات مكافحة الاحتيال والأمان' : 'Fraud prevention and security logs',
    language === 'ar' ? 'البيانات المُستَبقاة بموجب أوامر قضائية' : 'Data retained under court orders'
  ];

  return (
    <div className={`min-h-screen cgm-page`}>
      {/* Compact Hero Section */}
      <section className="relative overflow-hidden py-8 sm:py-10">
        <div className="absolute inset-0 pointer-events-none">
          <div className={`absolute inset-0 ${isDarkMode ? 'bg-gradient-to-r from-[#0F172A] to-[#1E293B]' : 'bg-gradient-to-r from-[#38BDF8] to-[#F97316]'}`} />
          <div className="absolute top-0 end-0 w-64 h-64 rounded-full bg-white/10 blur-3xl" />
          <div className="absolute bottom-0 start-0 w-48 h-48 rounded-full bg-white/10 blur-3xl" />
        </div>

        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-white/80 hover:text-white mb-4 transition-colors"
            data-testid="data-deletion-back-btn"
          >
            <ArrowLeft className="h-4 w-4 rtl:rotate-180" />
            <span className="text-sm">{language === 'ar' ? 'الرئيسية' : 'Home'}</span>
          </Link>

          <div className="text-center">
            <div className="w-16 h-16 mx-auto glass-card rounded-2xl flex items-center justify-center mb-4">
              <Trash2 className="h-8 w-8 text-white" />
            </div>
            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-3" data-testid="data-deletion-title">
              {language === 'ar' ? 'حذف بيانات المستخدم' : 'User Data Deletion'}
            </h1>
            <p className="text-base text-white/90 max-w-2xl mx-auto">
              {language === 'ar'
                ? 'اطلب حذف حسابك وبياناتك الشخصية من منصة CloudGM Business في أي وقت'
                : 'Request deletion of your account and personal data from CloudGM Business at any time'}
            </p>
            <p className="text-xs text-white/70 mt-3">
              {language === 'ar' ? 'آخر تحديث: فبراير 2026' : 'Last updated: February 2026'}
            </p>
          </div>
        </div>
      </section>

      {/* Quick Action Card */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 -mt-5">
        <div className={`rounded-2xl shadow-xl p-5 sm:p-6 border ${
          isDarkMode
            ? 'bg-[#131B2C] border-white/10'
            : 'bg-white border-black/5'
        }`}>
          <div className="flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-6">
            <div className="w-12 h-12 rounded-xl gradient-primary flex items-center justify-center flex-shrink-0">
              <Mail className="h-6 w-6 text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <h2 className={`text-base sm:text-lg font-bold mb-1 ${isDarkMode ? 'text-white' : 'text-[#0F172A]'}`}>
                {language === 'ar' ? 'أرسل طلبك مباشرة' : 'Send Your Request Directly'}
              </h2>
              <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`} dir="ltr">
                {SUPPORT_EMAIL}
              </p>
            </div>
            <div className="flex gap-2">
              <button
                onClick={handleCopyEmail}
                className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all border ${
                  isDarkMode
                    ? 'bg-white/5 border-white/10 text-white hover:bg-white/10'
                    : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100'
                }`}
                data-testid="copy-email-btn"
              >
                <Copy className="h-4 w-4" />
                {language === 'ar' ? 'نسخ' : 'Copy'}
              </button>
              <a
                href={mailtoLink}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-white gradient-primary hover:opacity-90 transition-opacity"
                data-testid="send-email-btn"
              >
                <Mail className="h-4 w-4" />
                {language === 'ar' ? 'إرسال بريد' : 'Send Email'}
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-10">
        <div className={`rounded-2xl shadow-lg p-5 sm:p-8 space-y-6 border ${
          isDarkMode
            ? 'bg-[#131B2C] border-white/10'
            : 'bg-white border-black/5'
        }`}>
          {/* Introduction */}
          <div className={`border-b pb-6 ${isDarkMode ? 'border-white/10' : 'border-gray-100'}`}>
            <p className={`leading-relaxed text-base ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              {language === 'ar'
                ? 'نحترم في CloudGM Business حقّك الكامل في التحكّم ببياناتك الشخصية. توضّح هذه الصفحة كيف تطلب حذف حسابك وبياناتك من منصّتنا، والخطوات التي سنتّبعها لتنفيذ طلبك بشكل آمن وفي الإطار الزمني المحدّد.'
                : 'At CloudGM Business, we fully respect your right to control your personal data. This page explains how to request the deletion of your account and data from our platform, and the steps we will follow to fulfill your request securely and within the specified timeframe.'}
            </p>
          </div>

          {/* Steps */}
          <div>
            <h2 className={`text-lg sm:text-xl font-bold mb-4 ${isDarkMode ? 'text-white' : 'text-[#0F172A]'}`}>
              {language === 'ar' ? 'خطوات طلب حذف البيانات' : 'Data Deletion Request Steps'}
            </h2>
            <div className="space-y-4">
              {steps.map((step, index) => (
                <div
                  key={index}
                  className={`flex gap-4 sm:gap-5 p-4 rounded-xl transition-all duration-300 hover:-translate-y-0.5 ${
                    isDarkMode
                      ? 'bg-white/5 hover:bg-white/10 border border-white/5'
                      : 'bg-gray-50 hover:bg-gray-100/80 border border-gray-100'
                  }`}
                >
                  <div className="flex-shrink-0">
                    <div className="relative">
                      <div className="w-11 h-11 rounded-xl gradient-primary flex items-center justify-center">
                        <step.icon className="h-5 w-5 text-white" />
                      </div>
                      <div className={`absolute -top-1.5 -end-1.5 w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold ${
                        isDarkMode ? 'bg-[#0B0F19] text-white border border-white/20' : 'bg-white text-[#0F172A] border border-gray-200'
                      }`}>
                        {index + 1}
                      </div>
                    </div>
                  </div>
                  <div className="flex-1">
                    <h3 className={`text-base font-bold mb-1.5 ${isDarkMode ? 'text-white' : 'text-[#0F172A]'}`}>
                      {step.title}
                    </h3>
                    <p className={`leading-relaxed text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                      {step.content}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Two Column Info */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Data to be deleted */}
            <div className={`rounded-xl p-5 border ${
              isDarkMode
                ? 'bg-red-500/5 border-red-500/20'
                : 'bg-red-50 border-red-100'
            }`}>
              <div className="flex items-center gap-3 mb-3">
                <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-red-500 to-rose-600 flex items-center justify-center">
                  <Trash2 className="h-4 w-4 text-white" />
                </div>
                <h3 className={`font-bold text-sm ${isDarkMode ? 'text-white' : 'text-[#0F172A]'}`}>
                  {language === 'ar' ? 'البيانات التي سيتم حذفها' : 'Data That Will Be Deleted'}
                </h3>
              </div>
              <ul className="space-y-2">
                {dataToBeDeleted.map((item, i) => (
                  <li key={i} className={`flex items-start gap-2 text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    <CheckCircle2 className={`h-4 w-4 mt-0.5 flex-shrink-0 ${isDarkMode ? 'text-red-400' : 'text-red-500'}`} />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Data retained */}
            <div className={`rounded-xl p-5 border ${
              isDarkMode
                ? 'bg-amber-500/5 border-amber-500/20'
                : 'bg-amber-50 border-amber-100'
            }`}>
              <div className="flex items-center gap-3 mb-3">
                <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center">
                  <ShieldAlert className="h-4 w-4 text-white" />
                </div>
                <h3 className={`font-bold text-sm ${isDarkMode ? 'text-white' : 'text-[#0F172A]'}`}>
                  {language === 'ar' ? 'بيانات قد تُحفظ لأسباب قانونية' : 'Data Retained for Legal Reasons'}
                </h3>
              </div>
              <ul className="space-y-2">
                {dataRetained.map((item, i) => (
                  <li key={i} className={`flex items-start gap-2 text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    <CheckCircle2 className={`h-4 w-4 mt-0.5 flex-shrink-0 ${isDarkMode ? 'text-amber-400' : 'text-amber-500'}`} />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Important Notice */}
          <div className={`rounded-xl p-5 border ${
            isDarkMode
              ? 'bg-amber-500/10 border-amber-500/30'
              : 'bg-amber-50 border-amber-200'
          }`}>
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center flex-shrink-0">
                <AlertTriangle className="h-5 w-5 text-white" />
              </div>
              <div>
                <h3 className={`font-bold mb-2 ${isDarkMode ? 'text-amber-400' : 'text-amber-800'}`}>
                  {language === 'ar' ? 'تنبيه مهم' : 'Important Notice'}
                </h3>
                <p className={`text-sm ${isDarkMode ? 'text-amber-300/80' : 'text-amber-700'}`}>
                  {language === 'ar'
                    ? 'حذف الحساب عملية نهائية ولا يمكن التراجع عنها. سيتم فقدان جميع متاجرك، منتجاتك، محادثاتك، ورصيدك المتبقي (إن وُجد) بشكل دائم. يُرجى التأكد من سحب أي أموال أو تنزيل نسخة من بياناتك قبل تقديم الطلب.'
                    : 'Account deletion is a final and irreversible action. All your stores, products, conversations, and any remaining balance will be permanently lost. Please make sure to withdraw any funds or download a copy of your data before submitting the request.'}
                </p>
              </div>
            </div>
          </div>

          {/* Contact */}
          <div className={`rounded-xl p-5 border ${
            isDarkMode
              ? 'bg-gradient-to-r from-[#38BDF8]/10 to-[#F97316]/10 border-white/10'
              : 'bg-gradient-to-r from-[#38BDF8]/5 to-[#F97316]/5 border-[#38BDF8]/20'
          }`}>
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-xl gradient-primary flex items-center justify-center">
                <Mail className="h-5 w-5 text-white" />
              </div>
              <h2 className={`text-lg font-bold ${isDarkMode ? 'text-white' : 'text-[#0F172A]'}`}>
                {language === 'ar' ? 'تواصل معنا' : 'Contact Us'}
              </h2>
            </div>
            <p className={`mb-4 text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
              {language === 'ar'
                ? 'للاستفسار عن حالة طلبك أو لأي سؤال متعلق بحذف البيانات، تواصل معنا عبر:'
                : 'For inquiries about your request status or any question related to data deletion, contact us via:'}
            </p>
            <div className={`space-y-2 text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
              <p><strong className="text-gradient">{language === 'ar' ? 'البريد الإلكتروني:' : 'Email:'}</strong> support@cloudgm.business</p>
              <p><strong className="text-gradient">{language === 'ar' ? 'الهاتف:' : 'Phone:'}</strong> <span dir="ltr">+971 50 151 1917</span></p>
              <p><strong className="text-gradient">{language === 'ar' ? 'الموقع:' : 'Website:'}</strong> www.cloudgm.business</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
